import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsTabs from "../clients/Tools/kit/ToolsTabs";
import ToolsPageHeader from "../clients/Tools/kit/ToolsPageHeader";
import ToolsRowMenu from "../clients/Tools/kit/ToolsRowMenu";
import ToolsModal from "../clients/Tools/kit/ToolsModal";
import PlaybookBuildService from "../../services/PlaybookBuildService";
import PlaybookEditService from "../../services/PlaybookEditService";
import PlaybookLibraryService from "../../services/PlaybookLibraryService";
import SkillBuildService from "../../services/SkillBuildService";
import UtilityService from "../../services/UtilityService";
import AgentResults from "./AgentResults";
import AgentRunReport from "./AgentRunReport";
import PlaybookIdentity from "./PlaybookIdentity";
import PlaybookDetail from "./PlaybookDetail";
import PlaybookBuildPage from "./PlaybookBuildPage";
import PlaybookCategoryCard from "./PlaybookCategoryCard";
import PlaybookCategoryView from "./PlaybookCategoryView";
import AgentSourceIcon from "./AgentSourceIcon";
import AgentSkillList from "./AgentSkillList";
import AgentToolbox from "./AgentToolbox";
import AgentRulesPanel from "./AgentRulesPanel";
import AgentSkillPage from "./AgentSkillPage";
import SkillBuildPage from "./SkillBuildPage";
import BuildDrafts from "./BuildDrafts";
import RunPlaybookFlow from "./RunPlaybookFlow";

import "../clients/Tools/tools-theme.css";
import "../clients/Tools/tools-kit.css";
import "./playbooks-page.css";

const TABS = {
	results: "results",
	library: "library",
	skills: "skills",
	toolbox: "toolbox",
	rules: "rules",
};

const NEW_SKILL_ID = "new";

class PlaybooksPage extends WComponent {
	constructor(props) {
		super(props);
		const locationState = (props.location && props.location.state) || {};
		const returningTab = [TABS.library, TABS.skills].includes(locationState.openTab) ? locationState.openTab : TABS.results;
		this.state = {
			activeTab: returningTab,
			selectedCategoryId: locationState.categoryId || null,
			skillsCategoryId: locationState.skillsCategoryId || null,
			agents: [],
			categories: [],
			myBuilds: [],
			isLoadingAgents: true,
			loadError: null,
			isStartingBuild: false,
			isStartingSkill: false,
			buildError: null,
			librarySearch: "",
			libraryKind: "all",
			runPlaybookId: null,
			isChoosingRun: false,
			archivingAgent: null,
			isArchiving: false,
			archiveError: null,
		};
	}

	componentDidMount() {
		this.fetchAgents();
	}

	fetchAgents = async () => {
		try {
			const [agents, myBuilds, categories] = await Promise.all([
				PlaybookLibraryService.listPlaybooks(),
				PlaybookBuildService.listMyBuilds(),
				PlaybookLibraryService.listCategories(),
			]);
			await this.update({ agents, myBuilds, categories, isLoadingAgents: false, loadError: null });
		} catch (error) {
			await this.update({
				isLoadingAgents: false,
				loadError: UtilityService.describeRequestError({ error, fallback: "The playbooks could not be loaded." }),
			});
		}
	};

	onStartBuild = async ({ playbookId = null, categoryId = null }) => {
		await this.update({ isStartingBuild: true, buildError: null });
		try {
			const session = await PlaybookBuildService.startBuild({ playbookId, categoryId });
			await this.update({ isStartingBuild: false });
			this.props.history.push(`/playbooks/build/${session.id}`);
		} catch (error) {
			await this.update({
				isStartingBuild: false,
				buildError: UtilityService.describeRequestError({ error, fallback: "The build conversation could not be opened." }),
			});
		}
	};

	onOpenBuild = ({ sessionId }) => {
		this.props.history.push(`/playbooks/build/${sessionId}`);
	};

	onSelectTab = activeTab => {
		this.update({ activeTab });
	};

	onOpenRun = ({ runId }) => {
		const { history, location } = this.props;
		history.push({ pathname: `/playbooks/runs/${runId}`, state: { resultsQuery: location.search } });
	};

	onBackToResults = () => {
		const { history, location } = this.props;
		const resultsQuery = location.state && location.state.resultsQuery ? location.state.resultsQuery : "";
		this.update({ activeTab: TABS.results });
		history.push(`/playbooks${resultsQuery}`);
	};

	onOpenAgent = ({ playbookId }) => {
		this.props.history.push({ pathname: `/playbooks/playbook/${playbookId}`, state: { categoryId: this.state.selectedCategoryId } });
	};

	onBackToLibrary = () => {
		const { location } = this.props;
		const categoryId = location.state && location.state.categoryId ? location.state.categoryId : null;
		this.update({ activeTab: TABS.library, selectedCategoryId: categoryId });
		this.fetchAgents();
		this.props.history.push({ pathname: "/playbooks", state: { openTab: TABS.library, categoryId } });
	};

	onOpenSkill = ({ skillId }) => {
		const { skillsCategoryId } = this.state;
		this.props.history.push({ pathname: `/playbooks/skill/${skillId}`, state: { skillsCategoryId } });
	};

	onNewSkill = async () => {
		const { skillsCategoryId } = this.state;
		await this.update({ isStartingSkill: true, buildError: null });
		try {
			const session = await SkillBuildService.startSkillBuild({ categoryId: skillsCategoryId ? Number(skillsCategoryId) : null });
			await this.update({ isStartingSkill: false });
			this.props.history.push({ pathname: `/playbooks/skills/build/${session.id}`, state: { skillsCategoryId } });
		} catch (error) {
			await this.update({
				isStartingSkill: false,
				buildError: UtilityService.describeRequestError({ error, fallback: "The skill conversation could not be opened." }),
			});
		}
	};

	onOpenSkillDraft = ({ sessionId }) => {
		const { skillsCategoryId } = this.state;
		this.props.history.push({ pathname: `/playbooks/skills/build/${sessionId}`, state: { skillsCategoryId } });
	};

	onReviewSkillDraft = ({ session }) => {
		const { history, location } = this.props;
		const skillsCategoryId = location.state && location.state.skillsCategoryId ? location.state.skillsCategoryId : null;
		const prefill = SkillBuildService.skillDraftForEditor({ session });
		history.push({ pathname: `/playbooks/skill/${NEW_SKILL_ID}`, state: { prefill, skillSessionId: session.id, skillsCategoryId } });
	};

	onWriteSkillByHand = ({ categoryId }) => {
		const { history, location } = this.props;
		const skillsCategoryId = location.state && location.state.skillsCategoryId ? location.state.skillsCategoryId : null;
		history.push({ pathname: `/playbooks/skill/${NEW_SKILL_ID}`, state: { prefill: { categoryId }, skillsCategoryId } });
	};

	onSkillCreated = async ({ skillId }) => {
		const { history, location } = this.props;
		const returnTo = location.state && location.state.returnTo;
		if (returnTo) {
			history.push({ pathname: returnTo, state: { addedFromDraft: { skillId, draftId: location.state.draftId } } });
			return;
		}
		const skillSessionId = location.state && location.state.skillSessionId ? location.state.skillSessionId : null;
		if (skillSessionId) {
			try {
				await SkillBuildService.finishSkillBuild({ sessionId: skillSessionId, skillId });
			} catch (error) {
				const reason = UtilityService.describeRequestError({ error, fallback: "the request failed" });
				UtilityService.alert({ type: "error", text: `The skill was saved. Its build conversation could not be marked as saved (${reason}), so it still shows as a draft: don't save that draft again.` });
			}
		}
		const skillsCategoryId = location.state && location.state.skillsCategoryId ? location.state.skillsCategoryId : null;
		history.replace({ pathname: `/playbooks/skill/${skillId}`, state: { skillsCategoryId } });
	};

	onBackToSkills = () => {
		const { history, location } = this.props;
		const skillsCategoryId = location.state && location.state.skillsCategoryId ? location.state.skillsCategoryId : null;
		this.update({ activeTab: TABS.skills, skillsCategoryId });
		this.fetchAgents();
		history.push({ pathname: "/playbooks", state: { openTab: TABS.skills, skillsCategoryId } });
	};

	onSelectSkillsCategory = ({ categoryId }) => {
		this.update({ skillsCategoryId: categoryId });
		this.props.history.replace({ pathname: "/playbooks", state: { openTab: TABS.skills, skillsCategoryId: categoryId } });
	};

	onOpenCategory = ({ categoryId }) => {
		this.update({ selectedCategoryId: categoryId, librarySearch: "", libraryKind: "all" });
		this.props.history.replace({ pathname: "/playbooks", state: { openTab: TABS.library, categoryId } });
	};

	onBackToCategories = () => {
		this.update({ selectedCategoryId: null });
		this.props.history.replace({ pathname: "/playbooks", state: { openTab: TABS.library } });
	};

	onRunPlaybook = ({ agent }) => {
		this.update({ runPlaybookId: agent.id, isChoosingRun: true });
	};

	onAskToArchive = ({ agent }) => {
		this.update({ archivingAgent: agent, archiveError: null });
	};

	onCloseArchive = () => {
		this.update({ archivingAgent: null, archiveError: null });
	};

	onArchivePlaybook = async () => {
		const { archivingAgent } = this.state;
		await this.update({ isArchiving: true, archiveError: null });
		try {
			await PlaybookEditService.archivePlaybook({ playbookId: archivingAgent.id });
			await this.update({ isArchiving: false, archivingAgent: null });
			await this.fetchAgents();
		} catch (error) {
			await this.update({
				isArchiving: false,
				archiveError: UtilityService.describeRequestError({ error, fallback: "The playbook could not be archived." }),
			});
		}
	};

	onRowKeyDown = ({ event, agent }) => {
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		this.onOpenAgent({ playbookId: agent.id });
	};

	renderSourceChips({ agent }) {
		const version = agent.current_version;
		const toolCount = (version.tool_names || []).length;
		const sources = agent.sources || [];
		return (
			<React.Fragment>
				{sources.map(source => (
					<span key={source.key} className="tk-chip" title={source.isRequired ? "Required" : "Helpful"}>
						<AgentSourceIcon icon={source.icon} size="small" />
						{source.label}
					</span>
				))}
				{toolCount > 0 && <span className="tk-chip tk-chip--muted">{UtilityService.describeCount({ count: toolCount, singular: "tool" })}</span>}
			</React.Fragment>
		);
	}

	renderAgent = agent => {
		const version = agent.current_version;
		const aside = (
			<React.Fragment>
				<span className="playbook-identity__aside-strong">v{version.version_number}</span>
				<span>{PlaybookLibraryService.describeLastRun({ lastRunAt: agent.lastRunAt })}</span>
			</React.Fragment>
		);
		const action = (
			<React.Fragment>
				<button
					type="button"
					className="tk-button tk-button--secondary tk-button--compact"
					onClick={event => {
						event.stopPropagation();
						this.onRunPlaybook({ agent });
					}}
				>
					<Icon.Play size={13} />
					Run
				</button>
				<span onClick={event => event.stopPropagation()}>
					<ToolsRowMenu
						items={[
							{ label: "Open", onClick: () => this.onOpenAgent({ playbookId: agent.id }) },
							{ label: "Run on a client", onClick: () => this.onRunPlaybook({ agent }) },
							{ label: "Change with the guide", onClick: () => this.onStartBuild({ playbookId: agent.id }) },
							{ label: "Archive", danger: true, onClick: () => this.onAskToArchive({ agent }) },
						]}
					/>
				</span>
			</React.Fragment>
		);

		return (
			<div
				key={agent.id}
				role="button"
				tabIndex={0}
				className="playbooks-page__agent"
				onClick={() => this.onOpenAgent({ playbookId: agent.id })}
				onKeyDown={event => this.onRowKeyDown({ event, agent })}
			>
				<PlaybookIdentity agent={agent} chips={this.renderSourceChips({ agent })} aside={aside} action={action} />
			</div>
		);
	};

	renderDrafts() {
		const { myBuilds } = this.state;
		const playbookDrafts = myBuilds.filter(build => build.kind !== "skill");

		return (
			<BuildDrafts
				drafts={playbookDrafts}
				describeDraft={({ draft }) => (draft.draftVersionNumber ? `draft v${draft.draftVersionNumber}` : "not built yet")}
				onOpen={this.onOpenBuild}
			/>
		);
	}

	renderCategories() {
		const { categories } = this.state;
		const ordered = PlaybookLibraryService.orderCategoriesForLibrary({ categories });

		return (
			<div className="playbook-category-grid">
				{ordered.map(category => (
					<PlaybookCategoryCard
						key={category.id}
						category={category}
						isEmpty={category.agentCount === 0}
						countText={
							category.agentCount === 0 ? "No playbooks yet" : UtilityService.describeCount({ count: category.agentCount, singular: "playbook" })
						}
						onClick={() => this.onOpenCategory({ categoryId: category.id })}
					/>
				))}
			</div>
		);
	}

	renderCategory({ category }) {
		const { agents, isStartingBuild, librarySearch, libraryKind } = this.state;
		const agentsInCategory = PlaybookLibraryService.agentsInCategory({ agents, categoryId: category.id });
		const shownAgents = PlaybookLibraryService.playbooksMatching({ playbooks: agentsInCategory, search: librarySearch, kind: libraryKind });
		const isNarrowed = librarySearch.trim().length > 0 || libraryKind !== "all";

		return (
			<PlaybookCategoryView
				category={category}
				onBack={this.onBackToCategories}
				action={
					<button
						type="button"
						className="tk-button tk-button--primary"
						onClick={() => this.onStartBuild({ categoryId: category.id })}
						disabled={isStartingBuild}
					>
						<Icon.Plus size={14} />
						{isStartingBuild ? "Opening…" : "New playbook"}
					</button>
				}
				singular="playbook"
				totalCount={agentsInCategory.length}
				shownCount={shownAgents.length}
				search={librarySearch}
				onSearch={({ search }) => this.update({ librarySearch: search })}
				kind={libraryKind}
				onKind={({ kind }) => this.update({ libraryKind: kind })}
				isNarrowed={isNarrowed}
				emptyText={
					<React.Fragment>
						No playbooks in {category.name} yet. Choose <strong>New playbook</strong> and the guide will walk you through building the first one.
					</React.Fragment>
				}
				onClearNarrowing={() => this.update({ librarySearch: "", libraryKind: "all" })}
			>
				<div className="tk-card">{shownAgents.map(this.renderAgent)}</div>
			</PlaybookCategoryView>
		);
	}

	renderAreaActions() {
		const { activeTab, selectedCategoryId, skillsCategoryId, agents, isStartingBuild, isStartingSkill } = this.state;
		if (activeTab === TABS.results) {
			return (
				<button
					type="button"
					className="tk-button tk-button--primary"
					onClick={() => this.update({ runPlaybookId: null, isChoosingRun: true })}
					disabled={agents.length === 0}
				>
					<Icon.Play size={14} />
					Run a playbook
				</button>
			);
		}
		if (activeTab === TABS.library && !selectedCategoryId) {
			return (
				<button type="button" className="tk-button tk-button--primary" onClick={() => this.onStartBuild({})} disabled={isStartingBuild}>
					<Icon.Plus size={14} />
					{isStartingBuild ? "Opening…" : "New playbook"}
				</button>
			);
		}
		if (activeTab === TABS.skills && !skillsCategoryId) {
			return (
				<button type="button" className="tk-button tk-button--primary" onClick={this.onNewSkill} disabled={isStartingSkill}>
					<Icon.Plus size={14} />
					{isStartingSkill ? "Opening…" : "New skill"}
				</button>
			);
		}
		return null;
	}

	renderBuildError() {
		const { buildError } = this.state;
		if (!buildError) return null;
		return (
			<div className="tk-notice tk-notice--danger">
				<div className="tk-notice__title">
					<Icon.AlertTriangle size={14} />
					{buildError}
				</div>
			</div>
		);
	}

	renderLibrary() {
		const { categories, selectedCategoryId, isLoadingAgents, loadError } = this.state;
		if (isLoadingAgents) return <div className="tk-empty">Loading playbooks…</div>;
		if (loadError) return <div className="tk-notice tk-notice--danger">{loadError}</div>;
		const selectedCategory = categories.find(category => Number(category.id) === Number(selectedCategoryId));
		if (selectedCategory) {
			return (
				<div className="playbooks-page__library">
					{this.renderBuildError()}
					{this.renderCategory({ category: selectedCategory })}
				</div>
			);
		}

		return (
			<div className="playbooks-page__library">
				<p className="tk-page-intro">
					The firm's playbooks, by the kind of work they do. Open a category to run or change one; build a new one with the guide.
				</p>
				{this.renderBuildError()}
				{this.renderDrafts()}
				{this.renderCategories()}
			</div>
		);
	}

	render() {
		const {
			activeTab,
			agents,
			categories,
			myBuilds,
			isLoadingAgents,
			loadError,
			skillsCategoryId,
			isStartingSkill,
			isChoosingRun,
			runPlaybookId,
			archivingAgent,
			isArchiving,
			archiveError,
		} = this.state;
		const { history, location, match } = this.props;
		const runId = match && match.params ? match.params.runId : null;
		const playbookId = match && match.params ? match.params.playbookId : null;
		const sessionId = match && match.params ? match.params.sessionId : null;
		const skillId = match && match.params ? match.params.skillId : null;
		const skillSessionId = match && match.params ? match.params.skillSessionId : null;

		if (skillSessionId) {
			return (
				<div className="container">
					<div className="playbooks-page playbooks-page--wide">
						<SkillBuildPage
							key={skillSessionId}
							sessionId={Number(skillSessionId)}
							onBack={this.onBackToSkills}
							onReviewDraft={this.onReviewSkillDraft}
							onWriteByHand={this.onWriteSkillByHand}
							onOpenSkill={this.onOpenSkill}
						/>
					</div>
				</div>
			);
		}

		if (skillId) {
			const isNewSkill = skillId === NEW_SKILL_ID;
			return (
				<div className="container">
					<div className="playbooks-page">
						<AgentSkillPage
							key={skillId}
							skillId={isNewSkill ? null : Number(skillId)}
							isNew={isNewSkill}
							initialDraft={isNewSkill && location.state ? location.state.prefill : null}
							onBack={this.onBackToSkills}
							onSaved={this.onSkillCreated}
							onOpenPlaybook={this.onOpenAgent}
						/>
					</div>
				</div>
			);
		}

		if (sessionId) {
			return (
				<div className="container">
					<div className="playbooks-page playbooks-page--wide">
						<PlaybookBuildPage
							key={sessionId}
							sessionId={Number(sessionId)}
							history={history}
							addedFromDraft={location.state && location.state.addedFromDraft ? location.state.addedFromDraft : null}
							onBack={this.onBackToLibrary}
						/>
					</div>
				</div>
			);
		}

		if (runId) {
			return (
				<div className="container">
					<div className="playbooks-page">
						<AgentRunReport runId={Number(runId)} onBack={this.onBackToResults} backLabel="Back to results" />
					</div>
				</div>
			);
		}

		if (playbookId) {
			return (
				<div className="container">
					<div className="playbooks-page">
						{this.renderBuildError()}
						<PlaybookDetail
							key={playbookId}
							playbookId={Number(playbookId)}
							playbooks={agents}
							history={history}
							onBack={this.onBackToLibrary}
							onOpenRun={this.onOpenRun}
							onChangeWithGuide={() => this.onStartBuild({ playbookId: Number(playbookId) })}
						/>
					</div>
				</div>
			);
		}

		const tabs = [
			{ key: TABS.results, label: "Results" },
			{ key: TABS.library, label: "Playbooks", count: agents.length },
			{ key: TABS.skills, label: "Skills" },
			{ key: TABS.toolbox, label: "Toolbox" },
			{ key: TABS.rules, label: "Rules" },
		];

		return (
			<div className="container">
				<div className="playbooks-page">
					<ToolsPageHeader
						title="Playbooks"
						actions={this.renderAreaActions()}
						nav={<ToolsTabs tabs={tabs} activeKey={activeTab} onSelect={this.onSelectTab} />}
					/>
					<div className="playbooks-page__body">
						{activeTab === TABS.results && <AgentResults history={history} location={location} playbooks={agents} onOpenRun={this.onOpenRun} />}
						{activeTab === TABS.library && this.renderLibrary()}
						{activeTab === TABS.skills && (
							<AgentSkillList
								categories={categories}
								isLoadingCategories={isLoadingAgents}
								categoriesError={loadError}
								drafts={myBuilds.filter(build => build.kind === "skill")}
								selectedCategoryId={skillsCategoryId}
								isStartingSkill={isStartingSkill}
								onSelectCategory={this.onSelectSkillsCategory}
								onOpenSkill={this.onOpenSkill}
								onOpenDraft={this.onOpenSkillDraft}
								onNewSkill={this.onNewSkill}
							/>
						)}
						{activeTab === TABS.toolbox && <AgentToolbox />}
						{activeTab === TABS.rules && <AgentRulesPanel playbooks={agents} />}
					</div>
					<RunPlaybookFlow
						open={isChoosingRun}
						playbooks={agents}
						initialPlaybookId={runPlaybookId}
						history={history}
						onClose={() => this.update({ isChoosingRun: false })}
						onOpenRun={this.onOpenRun}
					/>
					<ToolsModal
						open={!!archivingAgent}
						onClose={this.onCloseArchive}
						title="Archive this playbook?"
						width={480}
						context={archivingAgent ? archivingAgent.current_version.name : null}
						footer={
							<div className="tk-modal-actions">
								<button type="button" className="tk-button tk-button--secondary" onClick={this.onCloseArchive}>
									Cancel
								</button>
								<button type="button" className="tk-button tk-button--danger" onClick={this.onArchivePlaybook} disabled={isArchiving}>
									{isArchiving ? "Archiving…" : "Archive"}
								</button>
							</div>
						}
					>
						<p className="playbooks-page__archive-note">
							It leaves the firm's list and its scheduled runs stop. Its versions, past runs and results stay where they are.
						</p>
						{archiveError && <div className="tk-notice tk-notice--danger">{archiveError}</div>}
					</ToolsModal>
				</div>
			</div>
		);
	}
}

export default PlaybooksPage;
